import { Injectable, Signal, computed, inject } from '@angular/core';

import { Store } from '@ngrx/store';
import { QueryClient } from '@tanstack/angular-query-experimental';

import {
  FeedPostsData,
  FeedPostsScope,
  feedPostsListsFilter,
  feedPostsQueryKey,
} from 'src/app/api/queries/feed.queries';
import {
  mergePostUpdate,
  togglePostThread,
} from 'src/app/shared/utils/post-thread.utils';
import {
  isShadowPostId,
  makeShadowPostId,
  realPostId,
} from 'src/app/shared/utils/shadow-post.utils';

import { CategoriesMap } from './feed.service';
import { Post } from '../models/core.models';
import { getContentFilters, selectFeed } from '../reducers';

const videoUrlTarget = (url: string): string | null => {
  try {
    return new URL(url).searchParams.get('target');
  } catch {
    return null;
  }
};

/**
 * The feed post list, imperatively.
 *
 * `injectFeedPostsQuery` is owned by `feed.component`; this is what the mutation
 * hooks, the results toolbar and the export panel use, through `QueryClient`.
 * It holds no copy of the list: every read and write goes at the same cache
 * entry the feed is rendering, and `scope` is the *same* computed the view hands
 * the query, so the key is provably the one being observed.
 *
 * Two different write targets, deliberately:
 * - **Server truth about one post** (`patchPost` and friends) goes at every
 *   cached page of the feed. A mutation can resolve after the user has paged,
 *   and an entry that does not hold the post is a no-op.
 * - **What is on screen** (selection, thread collapse) goes at the current
 *   scope only. Flipping `selected` on a still-cached previous page would leave
 *   the UI claiming a selection the server was never told about.
 */
@Injectable({ providedIn: 'root' })
export class FeedPostsService {
  private readonly store = inject(Store);
  private readonly queryClient = inject(QueryClient);

  private readonly feed = this.store.selectSignal(selectFeed);
  private readonly contentFilters = this.store.selectSignal(getContentFilters);

  /**
   * The key the list is cached under. Deep-equal rebuilds are free — TanStack
   * hashes keys structurally, so a new `getContentFilters` object with the same
   * contents is the same query.
   */
  readonly scope: Signal<FeedPostsScope | null> = computed(() => {
    const feedId = this.feed()?.id;
    if (!feedId) return null;

    return { feedId, contentFilters: this.contentFilters() };
  });

  /** The rendered entry map as cached right now, empty when nothing is loaded. */
  snapshot(): Map<string, Post> {
    return this.data()?.posts ?? new Map<string, Post>();
  }

  list(): Post[] {
    return [...this.snapshot().values()];
  }

  byIds(postIds: string[]): Post[] {
    const posts = this.snapshot();

    return postIds
      .map((id) => posts.get(id))
      .filter((post): post is Post => post !== undefined);
  }

  totalItemsCount(): number {
    return this.data()?.totalItemsCount ?? 0;
  }

  /** The real ids of every selected, selectable entry. */
  selectedPostIds(): string[] {
    return [...this.snapshot().entries()]
      .filter(([id, post]) => post.selected === true && !isShadowPostId(id))
      .map(([id]) => id);
  }

  /**
   * The entry whose video attachment carries the same `target` query param as
   * the given player URL — the iframe "share video segment" message's lookup.
   */
  byVideoUrlTarget(videoUrl: string): Post | null {
    const target = videoUrlTarget(videoUrl);
    if (!target) return null;

    for (const post of this.snapshot().values()) {
      const matches =
        post?.attachments?.some(
          (att) => videoUrlTarget(att.video_url ?? '') === target,
        ) ?? false;

      if (matches) return post;
    }

    return null;
  }

  /** Reload the feed's list — the Refresh button, auto-refresh, the write cues. */
  invalidate(): void {
    const feedId = this.feed()?.id;
    if (!feedId) return;

    this.queryClient.invalidateQueries(feedPostsListsFilter(feedId));
  }

  // ─── Server truth ────────────────────────────────────────────────────────

  /**
   * Merge a mutation's post into the canonical entry and every shadow copy of
   * it. `mergePostUpdate`'s fan-out is the whole point: patching only the real
   * id leaves stale copies on screen in a threaded feed.
   */
  patchPost(post: Post): void {
    this.patchPosts([post]);
  }

  /** Answers the real ids the list actually held. */
  patchPosts(posts: Post[]): string[] {
    if (!posts?.length) return [];

    const rendered = this.snapshot();
    const patched = posts
      .map((post) => realPostId(post.id))
      .filter((id) => rendered.has(id));

    this.writeEveryPage((current) => {
      const next = new Map(current);
      for (const post of posts) {
        mergePostUpdate(next, post);
      }
      return next;
    });

    return patched;
  }

  /**
   * The categorize dialog's response: one field, fanned out the same way.
   * `postIds` are real ids; the shadow copies of each are patched with them.
   */
  patchUserCategories(postIds: string[], categories: CategoriesMap): void {
    this.writeEveryPage((current) => {
      const next = new Map(current);

      for (const postId of postIds) {
        const newCategories = categories[postId];
        if (!newCategories) continue;

        const shadowPrefix = makeShadowPostId(postId, '');
        for (const key of next.keys()) {
          if (key !== postId && !key.startsWith(shadowPrefix)) continue;

          const post = next.get(key);
          if (!post) continue;

          next.set(key, { ...post, user_categories: newCategories });
        }
      }

      return next;
    });
  }

  /** A workflow transition: the response's `workflows`, fanned out. */
  patchWorkflows(post: Post): void {
    const realId = realPostId(post.id);
    const shadowPrefix = makeShadowPostId(realId, '');

    this.writeEveryPage((current) => {
      const next = new Map(current);

      for (const key of next.keys()) {
        if (key !== realId && !key.startsWith(shadowPrefix)) continue;

        const existing = next.get(key);
        if (!existing) continue;

        next.set(key, { ...existing, workflows: post.workflows });
      }

      return next;
    });
  }

  // ─── What is on screen ───────────────────────────────────────────────────

  /** One post's checkbox. Shadow entries are never selectable, so never here. */
  setSelected(postId: string, selected: boolean): void {
    this.writeCurrentPage((current) => {
      const post = current.get(postId);
      if (!post) return current;

      const next = new Map(current);
      next.set(postId, { ...post, selected });
      return next;
    });
  }

  /**
   * Select-all / unselect-all over the rendered page, answering the net change
   * so the caller can move `feed.count_selected_posts` — which stays reducer
   * state, because it is a feed-wide count the server seeds, not a property of
   * the loaded page. Parents and shadow copies are skipped: a shadow already has
   * its own selectable entry.
   */
  setAllSelected(selected: boolean): number {
    let delta = 0;

    this.writeCurrentPage((current) => {
      const next = new Map(current);

      for (const [id, post] of current.entries()) {
        if (post.is_parent) continue;
        if (isShadowPostId(id)) continue;
        if (post.selected === selected) continue;

        next.set(id, { ...post, selected });
        delta += selected ? 1 : -1;
      }

      return delta === 0 ? current : next;
    });

    return delta;
  }

  /** "Clear selection" — every entry, parents and shadows included. */
  clearSelection(): void {
    this.writeCurrentPage((current) => {
      const next = new Map(current);

      for (const [key, post] of current.entries()) {
        next.set(key, { ...post, selected: false });
      }

      return next;
    });
  }

  /** Collapse / expand one entry's whole ancestor chain. */
  toggleThread(postId: string): void {
    this.writeCurrentPage((current) => {
      if (!current.size) return current;

      const next = new Map(current);
      togglePostThread(next, postId);
      return next;
    });
  }

  // ─── Internals ───────────────────────────────────────────────────────────

  private data(): FeedPostsData | undefined {
    const scope = this.scope();
    if (!scope) return undefined;

    return this.queryClient.getQueryData<FeedPostsData>(
      feedPostsQueryKey(scope),
    );
  }

  private writeCurrentPage(
    update: (posts: Map<string, Post>) => Map<string, Post>,
  ): void {
    const scope = this.scope();
    if (!scope) return;

    this.writeKey(feedPostsQueryKey(scope), update);
  }

  private writeEveryPage(
    update: (posts: Map<string, Post>) => Map<string, Post>,
  ): void {
    const feedId = this.feed()?.id;
    if (!feedId) return;

    for (const query of this.queryClient
      .getQueryCache()
      .findAll(feedPostsListsFilter(feedId))) {
      this.writeKey(query.queryKey, update);
    }
  }

  /**
   * Every write here **preserves `dataUpdatedAt`**, so that timestamp keeps
   * meaning "when the server last answered". `feed.component` uses exactly that
   * to tell a fresh result set from a patch: without it, selecting a post would
   * look like a reload and yank the details pane back to the first entry.
   */
  private writeKey(
    queryKey: readonly unknown[],
    update: (posts: Map<string, Post>) => Map<string, Post>,
  ): void {
    const updatedAt =
      this.queryClient.getQueryState<FeedPostsData>(queryKey)?.dataUpdatedAt;

    this.queryClient.setQueryData<FeedPostsData>(
      queryKey,
      (data) => this.applied(data, update),
      updatedAt ? { updatedAt } : undefined,
    );
  }

  private applied(
    data: FeedPostsData | undefined,
    update: (posts: Map<string, Post>) => Map<string, Post>,
  ): FeedPostsData | undefined {
    if (!data) return data;

    const posts = update(data.posts);

    return posts === data.posts ? data : { ...data, posts };
  }
}
